import type { Category } from "../api/types";
import { fitName } from "./fits";

/** A piece that fits well, as the shopper describes it: brand and size first, the rest optional. */
export type PieceFields = {
  kind: string | null;
  brand: string;
  petite: boolean;
  curvy: boolean;
  /** Inseam for pants, length for the rest. Petite replaces it, so it's ignored while petite is on. */
  length: "Regular" | "Short" | "Tall";
  size: string;
  rise: string | null;
  shape: string | null;
  /** null until answered; true reveals the alteration picks. */
  altered: boolean | null;
  alts: string[];
  /** Anything they wish fit differently, in their own words. */
  wish: string;
  /** What that was read as (would-change ids), the text it was read from, and the ones they dropped. */
  wishRead: string[];
  wishFor: string;
  wishOff: string[];
  /** Optional rows the shopper chose to skip, so they count as answered. */
  skipped: string[];
};

export const emptyPiece = (kind: string | null = null): PieceFields => ({
  kind,
  brand: "",
  petite: false,
  curvy: false,
  length: "Regular",
  size: "",
  rise: null,
  shape: null,
  altered: null,
  alts: [],
  wish: "",
  wishRead: [],
  wishFor: "",
  wishOff: [],
  skipped: [],
});

export type Row = "brand" | "rise" | "shape" | "altered";
export const OPTIONAL_ROWS: Row[] = ["rise", "shape", "altered"];

export function rowsFor(cat: Category): Row[] {
  return cat === "pants" ? ["brand", "rise", "shape", "altered"] : ["brand", "shape", "altered"];
}

export function rowDone(p: PieceFields, r: Row): boolean {
  if (r === "brand") return !!p.brand.trim() && !!p.size.trim();
  if (p.skipped.includes(r)) return true;
  if (r === "altered") return p.altered !== null || !!cleanWish(p.wish);
  return !!p[r];
}

/** The wish as the server keeps it: single spaces, at most 500 characters. */
export const cleanWish = (t: string) => t.trim().split(/\s+/).join(" ").slice(0, 500);

/** The would-change ids to save: what was read from the wish, less what they dropped. */
export const keptWishes = (p: PieceFields) => (p.wishFor === cleanWish(p.wish) ? p.wishRead.filter((id) => !p.wishOff.includes(id)) : []);

/** Lengths each category is sold in besides petite. */
export function lengthsFor(cat: Category): PieceFields["length"][] {
  return cat === "pants" || cat === "skirt" ? ["Regular", "Short", "Tall"] : ["Regular", "Tall"];
}

/** The fit to save: "Petite Curvy", "Tall", "Curvy", or null for a plain regular. */
export function pieceFit(p: PieceFields): string | null {
  const len = p.petite ? "Petite" : p.length === "Regular" ? null : p.length;
  return fitName(len, p.curvy);
}

const num = (a: number, b: number) => Array.from({ length: b - a + 1 }, (_, i) => String(a + i));

/** Sizes to tap for a fit. A petite or short line usually stops a few sizes earlier; anything else is typed in. */
export function sizesFor(cat: Category, p: Pick<PieceFields, "petite" | "length">): string[] {
  if (cat === "pants") return p.petite ? num(23, 32) : p.length === "Short" ? num(23, 34) : p.length === "Tall" ? num(24, 37) : num(23, 37);
  if (cat === "top") return p.petite ? ["XXS", "XS", "S", "M", "L"] : ["XXS", "XS", "S", "M", "L", "XL", "XXL"];
  const dress = ["00", "0", "2", "4", "6", "8", "10", "12", "14", "16"];
  return p.petite ? dress.slice(0, 8) : dress;
}
